import { useEffect, useState } from 'react';

/**
 * Module-level toast pubsub. Mirrors the pattern in quota.ts (set-of-listeners
 * + useEffect-subscribed hook) so we keep React Context boilerplate out of the
 * call sites. Action helpers in state/projects.ts (and anywhere else surfacing
 * a user-visible failure) call `pushToast()` or the higher-level
 * `notifyActionError()` helper below; <ToastQueue /> in App.tsx renders
 * whatever is in the queue.
 *
 * Why a module-level store rather than App-level useState: action callers live
 * across many components (App, ProjectView, FileViewer, …) and pushing a toast
 * shouldn't require threading callbacks down the tree. Same trade-off the
 * existing quota store already accepts.
 */

export type ToastTone = 'info' | 'error' | 'success';

export interface Toast {
  readonly id: string;
  readonly tone: ToastTone;
  readonly message: string;
  /** Optional second line — e.g. the daemon's error body or an HTTP status. */
  readonly detail?: string;
  /**
   * Auto-dismiss after this many ms. 0 = sticky (caller dismisses).
   * Default 6000ms which gives ~6s read time.
   */
  readonly ttlMs: number;
}

type Listener = (snapshot: readonly Toast[]) => void;

let toasts: readonly Toast[] = [];
const listeners = new Set<Listener>();

function notify(): void {
  for (const fn of listeners) fn(toasts);
}

function genId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Enqueue a toast. Returns the id so callers can dismiss programmatically
 * (e.g. cancel the "Saving…" in-flight toast once the action succeeds).
 */
export function pushToast(input: {
  tone: ToastTone;
  message: string;
  detail?: string;
  ttlMs?: number;
  id?: string;
}): string {
  const id = input.id ?? genId();
  const ttlMs = input.ttlMs ?? 6000;
  const next: Toast = {
    id,
    tone: input.tone,
    message: input.message,
    ttlMs,
    ...(input.detail !== undefined ? { detail: input.detail } : {}),
  };
  toasts = [...toasts.filter((t) => t.id !== id), next];
  notify();
  if (ttlMs > 0) {
    setTimeout(() => dismissToast(id), ttlMs);
  }
  return id;
}

export function dismissToast(id: string): void {
  const before = toasts.length;
  toasts = toasts.filter((t) => t.id !== id);
  if (toasts.length !== before) notify();
}

/** Test helper — clears the queue without notifying about each id. */
export function clearAllToasts(): void {
  if (toasts.length === 0) return;
  toasts = [];
  notify();
}

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  fn(toasts);
  return () => {
    listeners.delete(fn);
  };
}

export function useToasts(): readonly Toast[] {
  const [snapshot, setSnapshot] = useState<readonly Toast[]>(toasts);
  useEffect(() => subscribe(setSnapshot), []);
  return snapshot;
}

/**
 * High-level helper for action-failure handling. Wraps the policy so each
 * caller doesn't have to remember to (a) interpolate a friendly message,
 * (b) special-case 401/403 with a redirect to /login, (c) special-case 413 on
 * file uploads.
 *
 *   const result = await createProject(input);
 *   if (!result.ok) { notifyActionError('Couldn\'t create project', result); return; }
 *
 * 401/403 is the trickiest path — boot-time auth gating (App.tsx) bounces
 * anonymous visitors to /login before they can click anything, but a session
 * can also expire mid-use or lose access after an allowlist/account change.
 * For that case we surface a focused toast and re-redirect to /login after a
 * short delay so the user has time to read the message.
 */
export function notifyActionError(
  label: string,
  result: { status: number; message: string },
): void {
  if (result.status === 401 || result.status === 403) {
    const forbidden = result.status === 403;
    pushToast({
      tone: 'error',
      message: forbidden ? 'Access changed' : 'Session expired',
      detail: forbidden
        ? 'This account no longer has access. Sign in with an allowed account or contact support.'
        : 'Please sign in again.',
      ttlMs: 4000,
    });
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      // Brief delay so the toast paints before the redirect tears down the
      // SPA. 800ms is enough to read the recovery label without feeling slow.
      setTimeout(() => {
        try {
          window.location.replace(
            forbidden ? '/login?error=access-forbidden' : '/login?error=session-expired',
          );
        } catch {
          /* test environments without a real navigation target */
        }
      }, 800);
    }
    return;
  }
  if (result.status === 413) {
    pushToast({
      tone: 'error',
      message: `${label} failed`,
      detail: 'File is too large.',
    });
    return;
  }
  if (result.status === 0) {
    pushToast({
      tone: 'error',
      message: `${label} failed`,
      detail: result.message || 'Network error — check your connection.',
    });
    return;
  }
  pushToast({
    tone: 'error',
    message: `${label} failed`,
    detail: result.message || `HTTP ${result.status}`,
  });
}
